import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import { showToast } from '../../components/toast-notification';
import { useVolatilityDropdownOptions } from '../../pages/otc-trader/hooks/use-volatility-dropdown-options';
import { useVolatilitySelectOptions } from '../../pages/otc-trader/hooks/use-volatility-select-options';
import { getDeltaFromKeys } from '../../pages/otc-trader/utils/calculate-conversion';
import { WebSocketContext } from '../websocket-context/websocket-provider';

import { getTokenPairName, QuickPricerContext } from './quick-pricer-provider';
import {
  IListUserVolSurfacesBySymbol,
  IListUserVolSurfacesBySymbolVols,
  ISelectParameters,
  IVolatilityData,
} from './types';

type ISurfaceMarkingProviderParams = {
  children: ReactNode;
  tab?: string;
};

type RowData = {
  tenor: string;
  [key: string]: string;
};

type ISurfaceMarkingProvider = {
  options: {
    tokenPairOption: {
      selectedItems: any;
      setSelectedItems: React.Dispatch<any>;
      handleChange: (options: any) => void;
    };
    surfaceOptions: {
      selectedItems: any;
      setSelectedItems: React.Dispatch<any>;
      handleChange: (options: any) => void;
    };
    spotSourceOptions: {
      selectedItems: any;
      setSelectedItems: React.Dispatch<any>;
      handleChange: (options: any) => void;
    };
    deltaOption: any;
    tenorOption: any;
  };
  volatilityOptions: ISelectParameters;
  setVolatilityOptions: React.Dispatch<React.SetStateAction<ISelectParameters>>;
  volatilityData: IVolatilityData;
  setVolatilityData: React.Dispatch<React.SetStateAction<IVolatilityData>>;
  volatilityTableData: RowData[];
  setVolatilityTableData: React.Dispatch<React.SetStateAction<RowData[]>>;
  volatilitySavedTableData: RowData[];
  setVolatilitySavedTableData: React.Dispatch<React.SetStateAction<RowData[]>>;
  volatilityTableValidationErrors: {
    [deltaId: string]: {
      [tenor: string]: string[];
    };
  };
  setVolatilityTableValidationErrors: React.Dispatch<
    React.SetStateAction<{
      [deltaId: string]: {
        [tenor: string]: string[];
      };
    }>
  >;
  getListTokens: () => void;
  createUserVolSurface: (
    symbol: string,
    source: string,
    tag: string,
    vols: IListUserVolSurfacesBySymbolVols[],
  ) => void;
  getUserVolSurface: (id: number) => void;
  getAllListRateSymbols: (symbol: string) => void;
  surfaceLoading: boolean;
  isUserVolSurfaceByIdLoading: boolean;
  isMarketPairTokenLoading: boolean;
  isCreateUserVolSurfaceLoading: boolean;
  tenorItems: string[];
  setTenorItems: React.Dispatch<React.SetStateAction<string[]>>;
  isLoadingTransformation: boolean;
  setIsLoadingTransformation: React.Dispatch<React.SetStateAction<boolean>>;
  savedDelta: string | undefined;
};

export const SurfaceMarkingContext = createContext({} as ISurfaceMarkingProvider);

const MARKET_PAIR_TOKENS = 'VOLATILITY_MARKET_PAIR_TOKENS';
const CREATE_USER_VOL_SURFACE = 'VOLATILITY_CREATE_USER_VOL_SURFACE';
const USER_VOL_SURFACES_BY_SYMBOL = 'VOLATILITY_USER_VOL_SURFACES_BY_SYMBOL';
const USER_VOL_SURFACE_BY_ID = 'VOLATILITY_USER_VOL_SURFACE_BY_ID';

export const SurfaceMarkingProvider = ({ children, tab }: ISurfaceMarkingProviderParams) => {
  const { sendMessage, subscribeToChannel, isConnected } = useContext(WebSocketContext);
  const [surfaceLoading, setSurfaceLoading] = useState(false);
  const [isUserVolSurfaceByIdLoading, setIsUserVolSurfaceByIdLoading] = useState(false);
  const [isMarketPairTokenLoading, setIsMarketPairTokenLoading] = useState(false);
  const [isCreateUserVolSurfaceLoading, setIsCreateUserVolSurfaceLoading] = useState(false);
  const [isLoadingTransformation, setIsLoadingTransformation] = useState(false);

  const tokenPair = getTokenPairName(tab);
  const { trades, onUpdateSurfaces } = useContext(QuickPricerContext);

  const makeId = (key: string) => `${key}-${tab || 'default'}`;
  const getId = (id: string) => id.replace(`-${tab || 'default'}`, '');

  const [volatilityData, setVolatilityData] = useState<IVolatilityData>({
    listUserVolSurfacesBySymbol: [],
    userVolSurfaceById: {} as IListUserVolSurfacesBySymbol,
    marketPairTokens: [],
    currentUserVolSurfaceId: null,
  });
  const [volatilityOptions, setVolatilityOptions] = useState<ISelectParameters>({
    tokenPair: '',
    surface: {} as IListUserVolSurfacesBySymbol,
    spotSource: '',
    delta: [],
    tenor: [],
  });
  const [volatilityTableData, setVolatilityTableData] = useState<RowData[]>([]);
  const [volatilitySavedTableData, setVolatilitySavedTableData] = useState<RowData[]>([]);
  const [volatilityTableValidationErrors, setVolatilityTableValidationErrors] = useState<{
    [deltaId: string]: { [tenor: string]: string[] };
  }>({});
  const tokenPairOption = useVolatilitySelectOptions(setVolatilityOptions, 'tokenPair');
  const surfaceOptions = useVolatilitySelectOptions(setVolatilityOptions, 'surface');
  const spotSourceOptions = useVolatilitySelectOptions(setVolatilityOptions, 'spotSource');
  const deltaOption: any = useVolatilityDropdownOptions(setVolatilityOptions, 'delta');
  const tenorOption: any = useVolatilityDropdownOptions(setVolatilityOptions, 'tenor');

  const [tenorItems, setTenorItems] = useState<string[]>([]);
  const isCreateSurface = useRef<boolean>(false);

  const savedDelta = useMemo(() => {
    return volatilitySavedTableData.length
      ? getDeltaFromKeys(Object.keys(volatilitySavedTableData[0]))
      : undefined;
  }, [volatilitySavedTableData]);

  const convertArrayToObjectsForTokenPair = (array: string[]) => {
    const uniqueItems = Array.from(new Set(array));

    return uniqueItems
      .map((item) => ({
        name: item,
        value: item,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const extractArraysRecursively = (obj: any): string[] => {
    if (!obj || typeof obj !== 'object') return [];
    if (Array.isArray(obj)) return obj;

    return Object.values(obj).reduce((acc: string[], value) => {
      return acc.concat(extractArraysRecursively(value));
    }, []);
  };

  const updateVolatilityData = useCallback((key: keyof IVolatilityData, newData: any) => {
    setVolatilityData((state) => ({ ...state, [key]: newData }));
  }, []);

  const handleResponse = useCallback(
    (response: any) => {
      if (!response) return;

      if (response.error) {
        showToast({
          message: 'Something went wrong, please try again.',
          type: 'error',
        });

        setSurfaceLoading(false);
        return;
      }

      const { id: idRaw, result } = response;

      const id = getId(idRaw);

      if (id === MARKET_PAIR_TOKENS) {
        const tokenPairList = convertArrayToObjectsForTokenPair(extractArraysRecursively(result));

        updateVolatilityData('marketPairTokens', tokenPairList);
        setIsMarketPairTokenLoading(false);

        if (tokenPair && tokenPairList.find((it) => it.value === tokenPair)) {
          setVolatilityOptions((state: any) => ({ ...state, tokenPair: tokenPair }));
        }

        return;
      }

      if (id === CREATE_USER_VOL_SURFACE) {
        if (response?.error && Object.keys(response?.error).length > 0) {
          setIsCreateUserVolSurfaceLoading(false);
          isCreateSurface.current = false;
          showToast({
            message: 'Something went wrong. We were unable to save your surface, please try again.',
            type: 'error',
          });
          return;
        }

        onUpdateSurfaces();

        isCreateSurface.current = true;
        updateVolatilityData('currentUserVolSurfaceId', result);
        showToast({ message: 'Your surface has been successfully saved.', type: 'success' });
      }

      if (id === USER_VOL_SURFACES_BY_SYMBOL) {
        updateVolatilityData('listUserVolSurfacesBySymbol', result);
        if (result.length > 0) {
          setVolatilityOptions((state) => ({ ...state, surface: result[0] }));
        }
        setSurfaceLoading(false);

        if (isCreateSurface.current) {
          setIsCreateUserVolSurfaceLoading(false);
          isCreateSurface.current = false;
        }
        return;
      }

      if (id === USER_VOL_SURFACE_BY_ID) {
        updateVolatilityData('userVolSurfaceById', result);
        setIsUserVolSurfaceByIdLoading(false);
        return;
      }
    },
    [trades],
  );

  const createUserVolSurface = useCallback(
    (symbol: string, source: string, tag: string, vols: IListUserVolSurfacesBySymbolVols[]) => {
      const payload = {
        jsonrpc: '2.0',
        id: CREATE_USER_VOL_SURFACE,
        method: 'create_user_vol_surface',
        params: {
          symbol,
          source,
          tag,
          vols,
        },
      };

      setIsCreateUserVolSurfaceLoading(true);
      sendMessage(JSON.stringify(payload));
      subscribeToChannel(CREATE_USER_VOL_SURFACE, handleResponse);
    },
    [handleResponse],
  );

  const getListTokens = useCallback(() => {
    const payload = {
      jsonrpc: '2.0',
      id: makeId(MARKET_PAIR_TOKENS),
      method: 'market_index_rate_sources',
      params: {},
    };

    setIsMarketPairTokenLoading(true);
    sendMessage(JSON.stringify(payload));
    subscribeToChannel(makeId(MARKET_PAIR_TOKENS), handleResponse);
  }, [sendMessage, isConnected]);

  const getUserVolSurface = useCallback(
    (id: number) => {
      const payload = {
        jsonrpc: '2.0',
        id: makeId(USER_VOL_SURFACE_BY_ID),
        method: 'user_vol_surface_by_id',
        params: {
          id,
        },
      };

      setIsUserVolSurfaceByIdLoading(true);
      sendMessage(JSON.stringify(payload));
      subscribeToChannel(makeId(USER_VOL_SURFACE_BY_ID), handleResponse);
    },
    [sendMessage, isConnected],
  );

  const getAllListRateSymbols = useCallback(
    (symbol: string) => {
      const payload = {
        jsonrpc: '2.0',
        id: makeId(USER_VOL_SURFACES_BY_SYMBOL),
        method: 'user_vol_surfaces_by_symbol',
        params: {
          symbol,
          offset: 0,
          direction: 'desc',
        },
      };

      setSurfaceLoading(true);
      sendMessage(JSON.stringify(payload));
      subscribeToChannel(makeId(USER_VOL_SURFACES_BY_SYMBOL), handleResponse);
    },
    [sendMessage, isConnected, setSurfaceLoading],
  );

  const options = {
    tokenPairOption,
    surfaceOptions,
    spotSourceOptions,
    deltaOption,
    tenorOption,
  };

  return (
    <SurfaceMarkingContext.Provider
      value={{
        options,
        surfaceLoading,
        isUserVolSurfaceByIdLoading,
        isMarketPairTokenLoading,
        isCreateUserVolSurfaceLoading,
        setVolatilityTableData,
        setVolatilitySavedTableData,
        volatilitySavedTableData,
        volatilityTableData,
        volatilityTableValidationErrors,
        setVolatilityTableValidationErrors,
        volatilityOptions,
        setVolatilityOptions,
        volatilityData,
        setVolatilityData,
        getListTokens,
        createUserVolSurface,
        getUserVolSurface,
        getAllListRateSymbols,
        tenorItems,
        setTenorItems,
        isLoadingTransformation,
        setIsLoadingTransformation,
        savedDelta,
      }}
    >
      {children}
    </SurfaceMarkingContext.Provider>
  );
};
